import * as React from 'react';
import styled from 'styled-components';
import { Typography } from '../Typography';
import { reactNodeToString } from '../../utils/reactNode';
import { logger } from '../../utils/logger';
import { filterNotEmpty } from '../../utils/listHelpers';
import { RichTextPresenter } from '../Select/MuiSelectElements/elements/RichTextPresenter';
import { InputAdornment } from '../Input/InputAdornment';
import { useIntlUds } from '../../translations/UdsIntlProvider';
import { MuiChip } from './MuiChip';
import { GroupedInputChip, InputChip, InputChipsProps, TreeItem } from './types';

export const InputChips: React.FC<React.PropsWithChildren<InputChipsProps>> = ({
  className,
  onClick,
  maxChips,
  values,
  group,
  groupTree,
  onDelete,
  onBeforeChipClick,
  onFocus,
  onBlur,
  loading,
  dataTest
}) => {
  const { format, intl } = useIntlUds();
  if (group && !groupTree) {
    // warning message for devs. don't translate!
    logger.warn('Grouping chips option is selected but you need to provide a "groupTree" (nested Nodes). ');
  }

  const chips = group && groupTree ? getGroupedChips(values, groupTree) : values;
  const limitedChips = maxChips ? chips.slice(0, maxChips) : chips;
  const howManyLeft = maxChips ? Math.max(chips.length - maxChips, 0) : 0;

  return (
    <StyledStartInputAdornment
      position="start"
      onClick={onClick}
      className={className}
      $limited={!!howManyLeft}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {limitedChips.map((chip: InputChip | GroupedInputChip) => {
        const label = reactNodeToString(chip.label);
        return (
          <MuiChip
            key={label}
            style={{
              marginTop: 1,
              marginRight: 8,
              overflow: 'hidden'
            }}
            clickable={false}
            data-test={dataTest}
            label={
              <RichTextPresenter
                selectedOption={{ label, value: '' }}
                visibleElementsWhenSelected={{ label: true }}
                variant="small"
                style={{ height: 24 }}
              />
            }
            tabIndex={0}
            onMouseDownCapture={onBeforeChipClick}
            disabled={chip.disabled}
            onDelete={onDelete ? () => onDelete?.('value' in chip ? [chip.value] : chip.values) : undefined}
            size="small"
            isLoading={loading}
          />
        );
      })}
      {!!howManyLeft && (
        <StyledTypography>
          {format(intl.Chip.InputChips.howManyLeftLabel, { howManyLeft: `${howManyLeft}` })}
        </StyledTypography>
      )}
    </StyledStartInputAdornment>
  );
};

const StyledTypography = styled(Typography)`
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
  position: absolute;
  right: 0;
  top: 3px;
`;

const StyledStartInputAdornment = styled(InputAdornment)<{ $limited: boolean }>`
  && {
    grid-area: chips;
    height: auto;
    flex-flow: row wrap;
    max-height: 150px;
    overflow-x: hidden;
    overflow-y: auto;
    position: relative;
    padding-left: 8px;
    padding-right: ${({ $limited }) => ($limited ? '60px' : undefined)};
  }
`;

function getGroupedChips(chips: InputChip[], nested: TreeItem[]): GroupedInputChip[] {
  const groupedMap = groupIntoMap(nested, chips);

  return Array.from(groupedMap).map(([, values]) => toGroupedChip(values));
}

function toGroupedChip([parentChip, ...subChips]: InputChip[]): GroupedInputChip {
  const subGroupsLabels = subChips?.length
    ? `: ${subChips.map((chip) => reactNodeToString(chip.label)).join(', ')}`
    : '';
  return {
    label: `${reactNodeToString(parentChip?.label)}${subGroupsLabels}`,
    values: filterNotEmpty([parentChip?.value, ...(subChips ?? []).map((chip) => chip.value)])
  };
}

function groupIntoMap(
  tree: TreeItem[] | undefined,
  chips: InputChip[],
  selectedItems: Map<InputChip['value'], (InputChip | TreeItem)[]> = new Map<
    InputChip['value'],
    (InputChip | TreeItem)[]
  >(),
  firstSelectedItem?: InputChip['value']
) {
  const treeLength = tree?.length ?? 0;
  for (let i = 0; i < treeLength; i++) {
    const currentItem = tree?.[i];
    const selectedChip = chips.find((chip) => chip.value === currentItem?.value);
    let selected = firstSelectedItem;
    if (selectedChip) {
      if (firstSelectedItem) {
        const firstSelectedValues = selectedItems.get(firstSelectedItem) ?? [];
        const inputChips: (InputChip | TreeItem)[] = [...firstSelectedValues];
        if (currentItem) inputChips.push(currentItem);
        selectedItems.set(firstSelectedItem, inputChips);
      } else if (currentItem?.value) {
        selectedItems.set(currentItem?.value, [currentItem]);
        selected = currentItem?.value;
      }
    } // if child has children of its own, continue digging.
    if (currentItem?.children?.length) {
      groupIntoMap(tree?.[i]?.children, chips, selectedItems, selected);
    }
  }
  return selectedItems;
}
